import { useNavigate } from 'react-router-dom';
import SeoHead from '../../components/SeoHead';

const sections = [
  {
    id: 1,
    icon: 'ri-layout-line',
    color: 'emerald',
    title: 'Estructura y formato del CV europeo',
    content: [
      {
        subtitle: 'Longitud recomendada',
        text: 'El CV europeo estándar debe tener entre 1 y 2 páginas. Los reclutadores europeos dedican en promedio 6 segundos a la primera lectura, por lo que la concisión es clave. Más de 2 páginas solo se justifica con más de 15 años de experiencia muy relevante.',
      },
      {
        subtitle: 'Orden cronológico inverso',
        text: 'Siempre presenta tu experiencia del puesto más reciente al más antiguo. Este es el estándar universal en Europa. Comienza con tu posición actual o más reciente y retrocede en el tiempo.',
      },
      {
        subtitle: 'Fuente y diseño',
        text: 'Usa fuentes profesionales como Calibri, Arial o Garamond en tamaño 10-12pt. Márgenes de 2cm. Evita colores llamativos, fotografías (en países como Alemania o Países Bajos no se incluyen) y diseños excesivamente creativos salvo en sectores creativos.',
      },
    ],
  },
  {
    id: 2,
    icon: 'ri-user-settings-line',
    color: 'blue',
    title: 'Datos personales y perfil profesional',
    content: [
      {
        subtitle: 'Qué incluir',
        text: 'Nombre completo, ciudad de residencia (no dirección completa), teléfono con prefijo internacional (+34...), correo electrónico profesional y perfil de LinkedIn actualizado. Opcionalmente: portfolio o web personal si es relevante.',
      },
      {
        subtitle: 'Qué NO incluir',
        text: 'En la mayoría de países europeos NO se incluye: fecha de nacimiento, estado civil, fotografía (especialmente en Alemania, Países Bajos, Suiza), número de DNI ni información religiosa o política. Esto protege contra la discriminación.',
      },
      {
        subtitle: 'Resumen profesional (extracto)',
        text: 'Incluye 3-5 líneas al inicio del CV que resuman quién eres, tu especialización y tu propuesta de valor. Personalízalo para cada oferta. Ejemplo: "Ingeniero industrial con 8 años de experiencia en optimización de procesos en entornos de manufactura de alta precisión. Especializado en metodologías Lean y Six Sigma. Bilingüe español-inglés con experiencia en equipos multiculturales."',
      },
    ],
  },
  {
    id: 3,
    icon: 'ri-briefcase-4-line',
    color: 'amber',
    title: 'Experiencia profesional',
    content: [
      {
        subtitle: 'Cómo describir cada puesto',
        text: 'Para cada posición incluye: nombre del puesto, empresa, país y ciudad, fechas (mes/año – mes/año) y 3-5 logros o responsabilidades clave. Usa verbos de acción en pasado: "Lideré", "Implementé", "Reduje", "Aumenté".',
      },
      {
        subtitle: 'Cuantifica tus logros',
        text: 'Los reclutadores europeos valoran enormemente los datos concretos. En lugar de "Mejoré las ventas", escribe "Incrementé las ventas un 23% en 12 meses mediante la implementación de una nueva estrategia de fidelización". Los números generan credibilidad inmediata.',
      },
      {
        subtitle: 'Adapta el lenguaje al país',
        text: 'En Alemania valoran la precisión técnica y los títulos exactos. En Países Bajos aprecian la iniciativa y el trabajo en equipo horizontal. En Suiza destacan la polivalencia y el multilingüismo. Investiga la cultura empresarial del país destino.',
      },
    ],
  },
  {
    id: 4,
    icon: 'ri-graduation-cap-line',
    color: 'purple',
    title: 'Formación académica',
    content: [
      {
        subtitle: 'Equivalencias de títulos',
        text: 'Indica siempre el equivalente europeo de tu titulación. Un "Grado universitario" español equivale a un "Bachelor\'s Degree" (nivel 6 del Marco Europeo de Cualificaciones). Un Máster equivale a "Master\'s Degree" (nivel 7). Puedes solicitar la homologación oficial en el país de destino.',
      },
      {
        subtitle: 'Qué incluir',
        text: 'Nombre del título, institución, país, año de finalización y nota media si es destacada (superior a 8/10 o equivalente). Para titulaciones recientes (menos de 5 años), puedes incluir asignaturas relevantes o el TFG/TFM si es pertinente.',
      },
      {
        subtitle: 'Formación continua',
        text: 'En Europa se valora mucho el aprendizaje continuo. Incluye certificaciones relevantes, cursos especializados, bootcamps o formaciones online de plataformas reconocidas (Coursera, edX, LinkedIn Learning). Indica siempre el año de obtención.',
      },
    ],
  },
  {
    id: 5,
    icon: 'ri-translate-2',
    color: 'teal',
    title: 'Idiomas y competencias digitales',
    content: [
      {
        subtitle: 'Marco Común Europeo de Referencia (MCER)',
        text: 'Usa siempre el estándar europeo: A1-A2 (básico), B1-B2 (intermedio), C1-C2 (avanzado/nativo). Evita términos vagos como "nivel medio" o "conversacional". Si tienes certificados oficiales (IELTS, TOEFL, Goethe-Zertifikat, DELF), indícalos con la puntuación obtenida.',
      },
      {
        subtitle: 'El idioma del país de destino',
        text: 'Aunque muchas empresas europeas trabajan en inglés, conocer el idioma local es un diferenciador clave. En Alemania, el alemán es casi imprescindible en muchos sectores. En Países Bajos el inglés es muy aceptado. En Suiza, el alemán, francés o italiano según la región.',
      },
      {
        subtitle: 'Competencias digitales',
        text: 'Menciona herramientas específicas, no genéricas. En lugar de "manejo de Office", escribe "Excel avanzado (tablas dinámicas, macros VBA), Power BI, SAP S/4HANA". Cuanto más específico, más credibilidad. Incluye también herramientas de colaboración: Teams, Slack, Jira, Asana.',
      },
    ],
  },
  {
    id: 6,
    icon: 'ri-star-line',
    color: 'rose',
    title: 'Competencias y habilidades blandas',
    content: [
      {
        subtitle: 'Cómo presentarlas correctamente',
        text: 'Evita listas genéricas de habilidades como "trabajo en equipo, proactividad, liderazgo". En su lugar, demuéstralas con ejemplos concretos en la sección de experiencia. Los reclutadores europeos son escépticos ante afirmaciones sin evidencia.',
      },
      {
        subtitle: 'Competencias más valoradas en Europa',
        text: 'Adaptabilidad intercultural, comunicación en entornos multilingües, gestión de proyectos, pensamiento analítico, autonomía y capacidad de toma de decisiones. En sectores técnicos: resolución de problemas complejos y orientación a resultados.',
      },
      {
        subtitle: 'Sección de intereses (opcional)',
        text: 'Una breve sección de intereses puede humanizar tu CV y generar conexión con el entrevistador. Sé específico: en lugar de "viajes", escribe "viajes culturales por Europa Central, con estancias en Viena, Praga y Budapest". Evita intereses demasiado genéricos.',
      },
    ],
  },
  {
    id: 7,
    icon: 'ri-mail-send-line',
    color: 'indigo',
    title: 'Carta de presentación y candidatura',
    content: [
      {
        subtitle: 'La carta de presentación en Europa',
        text: 'En Alemania (Anschreiben) y Austria es prácticamente obligatoria y muy formal. En Países Bajos y países nórdicos es más breve e informal. En todos los casos debe estar personalizada para cada empresa y puesto, nunca genérica.',
      },
      {
        subtitle: 'Estructura de la carta',
        text: 'Párrafo 1: Por qué esta empresa y este puesto específico. Párrafo 2: Qué aportas tú (2-3 logros concretos). Párrafo 3: Disponibilidad y llamada a la acción. Máximo 1 página. Usa el nombre del responsable de selección si lo conoces.',
      },
      {
        subtitle: 'Formato de envío',
        text: 'Envía siempre en PDF para preservar el formato. Nombra el archivo de forma profesional: "NombreApellido_CV_Empresa.pdf". Asegúrate de que el PDF no supere 2MB. Revisa que no haya errores ortográficos, especialmente en el idioma del país de destino.',
      },
    ],
  },
  {
    id: 8,
    icon: 'ri-search-eye-line',
    color: 'orange',
    title: 'Optimización ATS y palabras clave',
    content: [
      {
        subtitle: 'Qué es un ATS',
        text: 'La mayoría de empresas europeas medianas y grandes usan sistemas ATS (Applicant Tracking System) para filtrar CVs automáticamente antes de que lleguen a un reclutador humano. Tu CV debe superar este filtro digital primero.',
      },
      {
        subtitle: 'Cómo optimizar para ATS',
        text: 'Usa las palabras clave exactas de la oferta de empleo. Si la oferta dice "gestión de proyectos ágiles", usa esa frase exacta. Evita tablas, columnas múltiples, headers/footers con información importante, imágenes y fuentes no estándar que los ATS no pueden leer correctamente.',
      },
      {
        subtitle: 'Personalización por oferta',
        text: 'Nunca envíes el mismo CV a todas las empresas. Dedica 15-20 minutos a adaptar el resumen profesional, las palabras clave y el orden de las competencias según cada oferta. Esta personalización puede multiplicar por 3 tu tasa de respuesta.',
      },
    ],
  },
];

const tips = [
  { icon: 'ri-check-double-line', text: 'Revisa la ortografía en el idioma del país de destino, no solo en español' },
  { icon: 'ri-check-double-line', text: 'Pide a alguien de confianza que lea tu CV antes de enviarlo' },
  { icon: 'ri-check-double-line', text: 'Actualiza tu LinkedIn para que sea coherente con tu CV' },
  { icon: 'ri-check-double-line', text: 'Incluye un enlace a tu perfil de LinkedIn en el CV' },
  { icon: 'ri-check-double-line', text: 'Adapta el CV al idioma del país si tu nivel lo permite' },
  { icon: 'ri-check-double-line', text: 'Guarda siempre una versión en inglés como respaldo universal' },
];

const colorMap: Record<string, { bg: string; text: string; border: string; light: string }> = {
  emerald: { bg: 'bg-emerald-600', text: 'text-emerald-600', border: 'border-emerald-200', light: 'bg-emerald-50' },
  blue: { bg: 'bg-sky-600', text: 'text-sky-600', border: 'border-sky-200', light: 'bg-sky-50' },
  amber: { bg: 'bg-amber-600', text: 'text-amber-600', border: 'border-amber-200', light: 'bg-amber-50' },
  purple: { bg: 'bg-violet-600', text: 'text-violet-600', border: 'border-violet-200', light: 'bg-violet-50' },
  teal: { bg: 'bg-teal-600', text: 'text-teal-600', border: 'border-teal-200', light: 'bg-teal-50' },
  rose: { bg: 'bg-rose-600', text: 'text-rose-600', border: 'border-rose-200', light: 'bg-rose-50' },
  indigo: { bg: 'bg-indigo-600', text: 'text-indigo-600', border: 'border-indigo-200', light: 'bg-indigo-50' },
  orange: { bg: 'bg-orange-600', text: 'text-orange-600', border: 'border-orange-200', light: 'bg-orange-50' },
};

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const guiaCVSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Cómo adaptar tu CV al mercado europeo',
  description: 'Guía completa para estructurar tu currículum según los estándares europeos y destacar en procesos de selección internacional en Alemania, Suiza, Países Bajos, Austria y Bélgica.',
  url: `${siteUrl}/guia-cv-europeo`,
  author: { '@type': 'Organization', name: 'BN ĀGENZIA' },
  publisher: {
    '@type': 'Organization',
    name: 'BN ĀGENZIA',
    url: siteUrl,
  },
  inLanguage: 'es',
  about: { '@type': 'Thing', name: 'Currículum Vitae europeo' },
  dateModified: '2025-07-14',
};

export default function GuiaCVEuropeoPage() {
  const navigate = useNavigate();

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 print:bg-white">
      <SeoHead
        title="Cómo adaptar tu CV al mercado europeo – Guía profesional | BN ĀGENZIA"
        description="Guía completa para estructurar tu CV según los estándares europeos: formato, datos personales, experiencia, idiomas, ATS y carta de presentación. Elaborada por expertos en selección internacional."
        keywords="CV europeo, currículum europeo, adaptar CV Europa, formato CV Alemania, CV internacional"
        canonical="/guia-cv-europeo"
        schema={guiaCVSchema}
      />
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm print:hidden">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/perfil?tab=recursos')}
              className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-[#1A2B4A] transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-lg"></i>
              Volver a recursos
            </button>
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-semibold">
                Recurso gratuito
              </span>
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 bg-[#1A2B4A] text-white text-sm font-medium rounded-lg hover:bg-[#2A3B5A] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-printer-line"></i>
                Imprimir / Guardar PDF
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-10 print:py-4 print:px-8">

        {/* Hero de la guía */}
        <div className="bg-gradient-to-r from-[#1A2B4A] to-[#2A3B5A] rounded-2xl p-10 mb-10 print:rounded-none print:mb-6">
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-file-text-line text-white text-3xl"></i>
            </div>
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="px-3 py-1 bg-emerald-400/20 text-emerald-300 border border-emerald-400/30 rounded-full text-xs font-semibold uppercase tracking-wide">
                  Guía profesional
                </span>
                <span className="text-white/50 text-xs">8 secciones · Lectura: ~15 min</span>
              </div>
              <h1 className="text-3xl font-bold text-white mb-3 leading-tight">
                Cómo adaptar tu CV al mercado europeo
              </h1>
              <p className="text-white/80 text-base leading-relaxed max-w-2xl">
                Claves para estructurar tu currículum según los estándares europeos y destacar en procesos de selección internacional. Guía elaborada por nuestro equipo de selección con experiencia en Alemania, Suiza, Países Bajos, Austria y Bélgica.
              </p>
            </div>
          </div>
        </div>

        {/* Índice */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-10 print:mb-6">
          <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
            <i className="ri-list-check-2 text-[#1A2B4A]"></i>
            Contenido de la guía
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {sections.map((section) => {
              const c = colorMap[section.color];
              return (
                <a
                  key={section.id}
                  href={`#seccion-${section.id}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <span className={`w-7 h-7 ${c.bg} text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0`}>
                    {section.id}
                  </span>
                  <span className="text-sm text-gray-700 group-hover:text-[#1A2B4A] font-medium transition-colors">
                    {section.title}
                  </span>
                </a>
              );
            })}
          </div>
        </div>

        {/* Secciones */}
        <div className="space-y-8">
          {sections.map((section) => {
            const c = colorMap[section.color];
            return (
              <div
                key={section.id}
                id={`seccion-${section.id}`}
                className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden print:break-inside-avoid"
              >
                {/* Cabecera de sección */}
                <div className={`${c.light} ${c.border} border-b px-6 py-5 flex items-center gap-4`}>
                  <div className={`w-11 h-11 ${c.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <i className={`${section.icon} text-white text-xl`}></i>
                  </div>
                  <div>
                    <span className={`text-xs font-semibold ${c.text} uppercase tracking-wide`}>
                      Sección {section.id}
                    </span>
                    <h2 className="text-lg font-bold text-gray-900 leading-tight">
                      {section.title}
                    </h2>
                  </div>
                </div>

                {/* Contenido */}
                <div className="p-6 space-y-6">
                  {section.content.map((item, idx) => (
                    <div key={idx} className="flex gap-4">
                      <div className={`w-1 rounded-full ${c.bg} flex-shrink-0 mt-1`} style={{ minHeight: '100%' }}></div>
                      <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-1.5">{item.subtitle}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Checklist final */}
        <div className="mt-10 bg-white rounded-xl border border-gray-200 shadow-sm p-8 print:break-inside-avoid">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 bg-[#1A2B4A] rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-checkbox-multiple-line text-white text-xl"></i>
            </div>
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Antes de enviar</span>
              <h2 className="text-lg font-bold text-gray-900">Checklist final</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {tips.map((tip, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                <i className="ri-check-double-line text-emerald-600 text-lg flex-shrink-0 mt-0.5"></i>
                <p className="text-sm text-gray-700 leading-relaxed">{tip.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA final */}
        <div className="mt-8 bg-gradient-to-r from-[#1A2B4A] to-[#2A3B5A] rounded-xl p-8 text-center print:hidden">
          <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-customer-service-2-line text-white text-2xl"></i>
          </div>
          <h3 className="text-xl font-bold text-white mb-2">¿Quieres que revisemos tu CV?</h3>
          <p className="text-white/80 text-sm mb-6 max-w-lg mx-auto leading-relaxed">
            Nuestro equipo puede revisar tu currículum y orientarte sobre cómo adaptarlo al mercado europeo de forma personalizada.
          </p>
          <button
            onClick={() => navigate('/#contacto')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#1A2B4A] text-sm font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-send-line"></i>
            Contactar con el equipo
          </button>
        </div>

        {/* Footer de la guía */}
        <div className="mt-8 text-center text-xs text-gray-400 pb-8 print:mt-4">
          <p>Guía elaborada por el equipo de BN Agenzia · Uso exclusivo para candidatos registrados</p>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          @page { margin: 1.5cm; }
          body { font-size: 11pt; }
        }
      `}</style>
    </div>
  );
}
